import { useEffect, useRef, useState, type UIEvent } from 'react'

import {
	flexRender,
	type ColumnDef,
	type Row,
	type Table as TanstackTable,
} from '@tanstack/react-table'

import { cx } from '@/libs/cva'

import { Table } from '../table'
import { DataTablePagination } from './data-table-pagination'
import { NoResults } from './empty-table'

export interface DataTableRootProps<TData>
	extends React.HTMLAttributes<HTMLDivElement> {
	table: TanstackTable<TData>
	columns: ColumnDef<TData, any>[]
	disableRowSelection?: boolean
	onRowClick?: (row: Row<TData>) => void
	noResults?: boolean
	noHeader?: boolean
	pagination?: boolean
	layout?: 'fill' | 'fit'
}

export function DataTableRoot<TData>({
	table,
	columns,
	// disableRowSelection,
	onRowClick,
	noResults = false,
	noHeader = false,
	pagination = true,
	layout = 'fit',
}: DataTableRootProps<TData>) {
	const [showStickyBorder, setShowStickyBorder] = useState(false)
	const scrollableRef = useRef<HTMLDivElement>(null)

	const hasSelect = columns.find((c) => c.id === 'select')
	const hasActions = columns.find((c) => c.id === 'actions')

	// const rowSelection = table.getState().rowSelection
	const {
		pageIndex,
		//  pageSize
	} = table.getState().pagination

	const colCount = columns.length - (hasSelect ? 1 : 0) - (hasActions ? 1 : 0)
	const colWidth = 100 / colCount

	const handleHorizontalScroll = (e: UIEvent<HTMLDivElement>) => {
		const scrollLeft = e.currentTarget.scrollLeft

		if (scrollLeft > 0) {
			setShowStickyBorder(true)
		} else {
			setShowStickyBorder(false)
		}
	}

	useEffect(() => {
		scrollableRef.current?.scroll({ top: 0, left: 0 })
	}, [pageIndex])

	return (
		<div
			className={cx('flex w-full flex-col overflow-hidden', {
				'flex flex-1 flex-col': layout === 'fill',
			})}
		>
			<div
				ref={scrollableRef}
				onScroll={handleHorizontalScroll}
				className={cx('w-full', {
					'min-h-0 flex-grow overflow-auto': layout === 'fill',
					'overflow-x-auto': layout === 'fit',
				})}
			>
				{!noResults ? (
					<Table className="relative w-full">
						{!noHeader && (
							<Table.Header className="border-t-0">
								{table.getHeaderGroups().map((headerGroup) => {
									return (
										<Table.Row
											key={headerGroup.id}
											className={cx({
												'relative border-b-0 [&_th:last-of-type]:w-[1%] [&_th:last-of-type]:whitespace-nowrap':
													hasActions,
												'[&_th:first-of-type]:w-[1%] [&_th:first-of-type]:whitespace-nowrap':
													hasSelect,
											})}
										>
											{headerGroup.headers.map((header, index) => {
												const isActionHeader = header.id === 'actions'
												const isSelectHeader = header.id === 'select'
												const isSpecialHeader = isActionHeader || isSelectHeader

												const firstHeader = headerGroup.headers.findIndex(
													(h) => h.id !== 'select'
												)
												const isFirstHeader =
													firstHeader !== -1
														? header.id === headerGroup.headers[firstHeader].id
														: index === 0

												const isStickyHeader = isSelectHeader || isFirstHeader

												return (
													<Table.HeaderCell
														data-table-header-id={header.id}
														key={header.id}
														style={{
															width: !isSpecialHeader
																? `${colWidth}%`
																: undefined,
														}}
														className={cx('bg-ui-bg-field-component-hover', {
															"sticky left-0 after:absolute after:inset-y-0 after:right-0 after:h-full after:w-px after:bg-transparent after:content-['']":
																isStickyHeader,
															'left-[68px]':
																isStickyHeader && hasSelect && !isSelectHeader,
															'after:bg-ui-border-base':
																showStickyBorder &&
																isStickyHeader &&
																!isSpecialHeader,
														})}
													>
														{flexRender(
															header.column.columnDef.header,
															header.getContext()
														)}
													</Table.HeaderCell>
												)
											})}
										</Table.Row>
									)
								})}
							</Table.Header>
						)}
						<Table.Body className="border-b-0">
							{table.getRowModel().rows.map((row) => {
								const hasOnClickCell = typeof onRowClick === 'function'
								const isRowDisabled = hasSelect && !row.getCanSelect()

								const isOdd = row.depth % 2 !== 0

								const cells = row.getVisibleCells()

								return (
									<Table.Row
										key={row.id}
										data-selected={row.getIsSelected()}
										className={cx(
											'group/row group relative transition-fg [&_td:last-of-type]:w-[1%] [&_td:last-of-type]:whitespace-nowrap',
											'has-[[data-row-link]:focus-visible]:bg-ui-bg-base-hover',
											'focus-visible:bg-ui-bg-base-hover',
											{
												'bg-ui-bg-subtle hover:bg-ui-bg-subtle-hover': isOdd,
												'cursor-pointer': hasOnClickCell,
												'bg-ui-bg-highlight hover:bg-ui-bg-highlight-hover':
													row.getIsSelected(),
												'!hover:bg-ui-bg-disabled !bg-ui-bg-disabled':
													isRowDisabled,
											}
										)}
									>
										{cells.map((cell, index) => {
											const visibleCells = row.getVisibleCells()
											const isSelectCell = cell.column.id === 'select'

											const firstCell = visibleCells.findIndex(
												(h) => h.column.id !== 'select'
											)
											const isFirstCell =
												firstCell !== -1
													? cell.column.id === visibleCells[firstCell].column.id
													: index === 0

											const isStickyCell = isSelectCell || isFirstCell

											/**
											 * If the table has nested rows, we need to offset the cell padding
											 * to indicate the depth of the row.
											 */
											const depthOffset =
												row.depth > 0 && isFirstCell
													? row.depth * 14 + 24
													: undefined

											const hasLeftOffset =
												isStickyCell && hasSelect && !isSelectCell

											const Inner = flexRender(
												cell.column.columnDef.cell,
												cell.getContext()
											)

											const isTabableLink = isFirstCell && !!hasOnClickCell
											const shouldRenderAsLink =
												!!hasOnClickCell && !isSelectCell

											return (
												<Table.Cell
													key={cell.id}
													className={cx(
														'bg-ui-bg-base-hover',
														'transition-fg group-data-[selected=true]/row:bg-ui-bg-highlight group-data-[selected=true]/row:group-hover/row:bg-ui-bg-highlight-hover',
														{
															'!pl-0 !pr-0': shouldRenderAsLink,
															"sticky left-0 transition-fg after:absolute after:inset-y-0 after:right-0 after:h-full after:w-px after:bg-transparent after:content-[''] group-hover/row:bg-ui-bg-base-hover group-has-[[data-row-link]:focus-visible]:bg-ui-bg-base-hover group-data-[selected=true]/row:bg-ui-bg-highlight group-data-[selected=true]/row:group-hover/row:bg-ui-bg-highlight-hover":
																isStickyCell,
															'bg-ui-bg-subtle group-hover/row:bg-ui-bg-subtle-hover':
																isOdd && isStickyCell,
															'left-[68px]': hasLeftOffset,
															'after:bg-ui-border-base':
																showStickyBorder &&
																isStickyCell &&
																!isSelectCell,
															'!hover:bg-ui-bg-disabled !bg-ui-bg-disabled':
																isRowDisabled,
														}
													)}
													style={{
														paddingLeft: depthOffset
															? `${depthOffset}px`
															: undefined,
													}}
												>
													{shouldRenderAsLink ? (
														<a
															onClick={() => onRowClick(row)}
															className="size-full outline-none"
															data-row-link
															tabIndex={isTabableLink ? 0 : -1}
														>
															<div
																className={cx(
																	'flex size-full items-center pr-6',
																	{
																		'pl-6': isTabableLink && !hasLeftOffset,
																	}
																)}
															>
																{Inner}
															</div>
														</a>
													) : (
														Inner
													)}
												</Table.Cell>
											)
										})}
									</Table.Row>
								)
							})}
						</Table.Body>
					</Table>
				) : (
					<div className={cx({ 'border-b': layout === 'fit' })}>
						<NoResults />
					</div>
				)}
			</div>
			{pagination && (
				<div className={cx({ 'border-t': layout === 'fill' })}>
					<DataTablePagination table={table} />
				</div>
			)}
		</div>
	)
}
